import cx from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import FilterIcon from 'src/assets/images/icons/FilterIcon';
import { SelectPickerOptionProps, SelectPickerValue } from 'src/components/interfaces';
import { FILTER_TYPE, ViewType } from '../../constants';
import { IFilters } from '../../constants/types';
import styles from './index.module.scss';

import { Button } from '@navi/web-ui/lib/primitives';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { getFormattedDateSubtractedByDays } from 'src/utils/DateHelper';
import Filter from '@cp/src/components/Filter';
import { RadioGroup } from '@navi/web-ui/lib/components';
import { DownloadIcon } from '@navi/web-ui/lib/icons';
import DateTimePickerComponent from '@cp/src/components/DateTimePicker/DateTimePickerComponent';
import { DATE_TIME_TYPE } from '@cp/src/components/DateTimePicker/constants';
import { getAvailabilityHistoryData, downloadAgentAvailabilityHistory } from '../../actions';
import { setRadioOption } from '../../reducers';
import { DEFAULT_OPTIONS } from '../../constants/index';

interface IAgentAvailabilityFilterProps {
  selectedFilter: SelectPickerOptionProps;
  type: FILTER_TYPE;
  shouldUpdatePageNumber?: boolean;
}

const AvailabilityHistoryFilters: React.FC<IFilters> = props => {
  const { params, updateAndNavigate } = props;
  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.agentAvailabilitySlice);
  const [agentNames, setAgentNames] = React.useState<SelectPickerOptionProps[]>([]);
  const userData = useSelector((state: RootState) => state.common.userData);

  const onSelectionChange = ({
    selectedFilter,
    type,
    shouldUpdatePageNumber = true
  }: IAgentAvailabilityFilterProps) => {
    if (!params) return;
    const updatedParams = { ...params };
    const processTLChange = (referenceId: string) => {
      const agentsUnderTl = filters?.reporteesDetails?.filter(
        agent => agent.tlReferenceId === referenceId
      );
      const agentsList =
        agentsUnderTl?.map(agent => ({
          label: agent.name,
          value: agent.referenceId
        })) || [];
      setAgentNames(agentsList);
      updatedParams['teamLeadId'] = referenceId;
      if (shouldUpdatePageNumber) {
        updatedParams['pageNo'] = '1';
      }
    };

    const processAgentChange = (referenceId: string) => {
      const currentSelectedAgent = filters?.reporteesDetails?.find(
        agent => agent.referenceId === referenceId
      );
      const tlForCurrentSelectedAgent = filters?.tlDetails?.find(
        tl => tl.referenceId === currentSelectedAgent?.tlReferenceId
      );
      if (tlForCurrentSelectedAgent) {
        updatedParams['teamLeadId'] = tlForCurrentSelectedAgent.referenceId;
        if (agentNames.length === 0) {
          processTLChange(tlForCurrentSelectedAgent.referenceId);
        }
      }
      updatedParams['agentId'] = referenceId as string;
      if (shouldUpdatePageNumber) {
        updatedParams['pageNo'] = '1';
      }
    };

    // for clearing in case of senior team lead
    if (
      params?.viewType === ViewType.SENIOR_TEAM_LEAD &&
      type === FILTER_TYPE.TEAM_LEAD &&
      Array.isArray(selectedFilter)
    ) {
      delete updatedParams['teamLeadId'];
      updateAndNavigate(updatedParams);
      return;
    }

    if (type === FILTER_TYPE.TEAM_LEAD) {
      processTLChange(selectedFilter.value as string);
    }
    if (type === FILTER_TYPE.AGENT) {
      processAgentChange(selectedFilter.value as string);
    }
    updateAndNavigate(updatedParams);
  };

  useEffect(() => {
    if (params?.teamLeadId) {
      onSelectionChange({
        selectedFilter: { value: params?.teamLeadId, label: '' },
        type: FILTER_TYPE.TEAM_LEAD,
        shouldUpdatePageNumber: false
      });
    }
    if (params?.agentId) {
      onSelectionChange({
        selectedFilter: { value: params?.agentId, label: '' },
        type: FILTER_TYPE.AGENT,
        shouldUpdatePageNumber: false
      });
    }
  }, [filters.reporteesDetails, filters.tlDetails]);

  const selectedTL = useMemo(
    () => filters?.tlDetails?.find(a => a.referenceId === params?.teamLeadId),
    [params, filters.tlDetails]
  );
  const selectedAgent = useMemo(
    () => filters?.reporteesDetails?.find(a => a.referenceId === params?.agentId),
    [params, filters.reporteesDetails]
  );

  const handleRadioChange = (event: React.SyntheticEvent) => {
    const value = (event?.target as HTMLInputElement)?.value;
    dispatch(setRadioOption(value));
    updateAndNavigate({ ...params, radioOption: value });
  };
  const maxInputDate = getFormattedDateSubtractedByDays(7);
  const dateChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = event?.target?.value || maxInputDate;
    const referenceId =
      params?.viewType === ViewType.SENIOR_TEAM_LEAD && params?.teamLeadId
        ? params.teamLeadId
        : userData?.referenceId || '';
    dispatch(getAvailabilityHistoryData(newDate, referenceId));
    updateAndNavigate({ ...params, startDate: newDate });
  };
  const handleDownload = () => {
    downloadAgentAvailabilityHistory({
      tlRefId: params?.teamLeadId,
      agentRefId: params?.agentId,
      radioOption: params?.radioOption
    });
  };
  return (
    <div className="flex justify-between items-center pb-[20px]">
      <div className="flex flex-row items-center gap-[8px]">
        <FilterIcon />
        {params?.viewType === ViewType.SENIOR_TEAM_LEAD && filters?.tlDetails?.length > 0 ? (
          <Filter
            containerClass={styles.agentNameFilter}
            key={params?.teamLeadId}
            title={selectedTL?.name || 'TL name'}
            options={filters?.tlDetails.map(tl => ({ label: tl.name, value: tl.referenceId }))}
            onSelectionChange={change =>
              onSelectionChange({
                selectedFilter: change as SelectPickerOptionProps,
                type: FILTER_TYPE.TEAM_LEAD
              })
            }
            isSingleSelect
            showSearchBar
            showCancelOption={false}
            selectedValue={params?.teamLeadId}
            selectedValues={params?.teamLeadId as unknown as SelectPickerValue[]}
            disabled={Boolean(params?.agentId)}
            titleClass={styles.agentNameFilterTitle}
            showTooltip
          />
        ) : null}
        <Filter
          containerClass={styles.agentNameFilter}
          key={params?.agentId}
          title={selectedAgent?.name || 'Agent name'}
          options={
            params?.teamLeadId
              ? agentNames
              : filters?.reporteesDetails?.map(agent => ({
                  label: agent.name,
                  value: agent.referenceId
                }))
          }
          onSelectionChange={change =>
            onSelectionChange({
              selectedFilter: change as SelectPickerOptionProps,
              type: FILTER_TYPE.AGENT
            })
          }
          isSingleSelect
          showSearchBar
          selectedValue={params?.agentId}
          selectedValues={params?.agentId as unknown as SelectPickerValue[]}
          titleClass={styles.agentNameFilterTitle}
          showTooltip
        />
        <DateTimePickerComponent
          type={DATE_TIME_TYPE.DATE}
          max={maxInputDate}
          value={params?.startDate || 'StartDate'}
          onChange={dateChangeHandler}
          inputClasses={styles.DatePicker}
          containerClasses={'!w-[140px]'}
        />
      </div>
      <div className="flex flex-row items-center gap-[24px]">
        <RadioGroup
          className="flex flex-row"
          onChange={handleRadioChange}
          value={params?.radioOption}
          name="cashCollected"
          options={DEFAULT_OPTIONS}
          labelClassName=""
        />
        <div className="h-[24px] w-[1px] bg-border" />
        <Button
          variant="text"
          size="medium"
          startAdornment={<DownloadIcon color="var(--blue-base)" />}
          onClick={handleDownload}
        >
          Download
        </Button>
      </div>
    </div>
  );
};

export default AvailabilityHistoryFilters;
